import type { ChecklistItem } from '../types/schema'
import type { HelperAssignedItem } from '../services/helper/helperAssignedItemsService'
import type { HelperMeetingItem } from '../services/helper/helperMeetingService'
import type { HelperChannelItem } from '../services/helper/helperChannelService'
import {
  DEBUG_ADMIN_COUNTERPARTY,
  DEBUG_HELPER_DISPLAY_NAME,
  DEBUG_HELPER_PHONE_E164,
  DEBUG_USER,
} from './fixtures'
import { DEBUG_TEAM_WORKSPACE_ID } from './workspace'

const nowIso = (): string => new Date().toISOString()
const HOUR = 60 * 60 * 1000

const baseChecklistItem = (overrides: Partial<ChecklistItem> & { id: string; title: string }): ChecklistItem => {
  const defaults = {
    parentContainerId: 'debug-goal-001',
    workspaceId: DEBUG_TEAM_WORKSPACE_ID,
    boardStatus: 'ASSIGNED' as ChecklistItem['boardStatus'],
    priority: 2,
    recordVersion: 1,
    description: 'Sample task seeded for debug bypass.',
    isCompleted: false,
    weightage: 5,
    position: { x: 0, y: 0 },
    prerequisites: [] as string[],
    blockedByExternal: null,
    assigneeId: DEBUG_USER.uid,
    assigneeName: DEBUG_HELPER_DISPLAY_NAME,
    createdById: DEBUG_ADMIN_COUNTERPARTY.uid,
    createdByName: DEBUG_ADMIN_COUNTERPARTY.displayName,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    lastUpdatedBy: DEBUG_ADMIN_COUNTERPARTY.uid,
    lastUpdatedByName: DEBUG_ADMIN_COUNTERPARTY.displayName,
  }
  return { ...defaults, ...overrides } as ChecklistItem
}

/**
 * Seeded checklist items assigned to the debug helper (phone DEBUG_HELPER_PHONE_E164).
 * Mix of statuses + due windows so the Tasks carousel + Calendar tab both have
 * material to render under debug bypass.
 */
export const getDebugHelperAssignedItems = (): HelperAssignedItem[] => {
  const now = Date.now()
  return [
    {
      ...baseChecklistItem({
        id: 'debug-task-onboarding',
        title: 'Confirm safety gear inventory',
        description: 'Walk the storage room and confirm helmets + harnesses are stocked. Photo proof required.',
        scheduleStartDate: new Date(now - 2 * HOUR).toISOString(),
        scheduleEndDate: new Date(now + 2 * HOUR).toISOString(),
        requiresProofFile: true,
        requiresLocationPin: true,
        completionCriteria: 'Upload one photo of each shelf.',
        priority: 1,
      }),
      effectiveStatus: 'ASSIGNED',
      updatedAtIso: nowIso(),
    },
    {
      ...baseChecklistItem({
        id: 'debug-task-call-vendor',
        title: 'Call vendor about delivery window',
        description: 'Confirm Friday morning delivery and update spreadsheet row.',
        scheduleStartDate: new Date(now).toISOString(),
        scheduleEndDate: new Date(now + 6 * HOUR).toISOString(),
        priority: 2,
      }),
      effectiveStatus: 'ACCEPTED',
      updatedAtIso: nowIso(),
    },
    {
      ...baseChecklistItem({
        id: 'debug-task-finalize-checklist',
        title: 'Finalize end-of-day site checklist',
        description: 'Run through the closing checklist and mark each item before locking up.',
        scheduleStartDate: new Date(now + 4 * HOUR).toISOString(),
        scheduleEndDate: new Date(now + 10 * HOUR).toISOString(),
        requiresProofFile: true,
        priority: 3,
      }),
      effectiveStatus: 'ONGOING',
      updatedAtIso: nowIso(),
    },
  ]
}

export const getDebugHelperMeetings = (): HelperMeetingItem[] => {
  const startsInMs = Date.now() + 90 * 60 * 1000
  return [
    {
      id: 'debug-meeting-standup',
      title: 'Helper sync with Workspace Admin',
      workspaceId: DEBUG_TEAM_WORKSPACE_ID,
      startsAt: startsInMs,
      endsAt: startsInMs + 30 * 60 * 1000,
      status: 'SCHEDULED',
      sourceProvider: 'GOOGLE_MEET',
      joinUrl: 'https://meet.example.com/debug-room',
      host: DEBUG_ADMIN_COUNTERPARTY.displayName,
      attendeeIds: [DEBUG_USER.uid, DEBUG_ADMIN_COUNTERPARTY.uid],
      agendaNodes: ['debug-task-onboarding', 'debug-task-call-vendor'],
      readinessChecklist: [
        { label: 'Bring the safety inventory photos', done: false },
        { label: 'Note any vendor delays', done: true },
      ],
    },
  ]
}

export const getDebugHelperChannels = (): HelperChannelItem[] => [
  {
    id: 'debug-channel-general',
    workspaceId: DEBUG_TEAM_WORKSPACE_ID,
    name: 'general',
    topic: `Day-to-day chat with the team. You joined as ${DEBUG_HELPER_PHONE_E164}.`,
    lastMessageAt: Date.now() - 5 * 60 * 1000,
    lastMessagePreview: 'See you on the 4 PM sync!',
    unreadCount: 1,
    autoSummarizeForHelpers: true,
  },
]

export interface DebugChatMessage {
  id: string
  authorId: string
  authorName: string
  text: string
  timestampIso: string
}

/** Window-scoped chat store so all module instances share one timeline. */
const CHAT_STORE_KEY = '__DEBUG_CHAT_MESSAGES__'
const CHAT_EVENT = 'debug-chat-store'

const getChatStore = (): DebugChatMessage[] => {
  if (typeof window === 'undefined') return []
  const existing = (window as unknown as Record<string, unknown>)[CHAT_STORE_KEY]
  if (Array.isArray(existing)) return existing as DebugChatMessage[]
  const now = Date.now()
  const seeded: DebugChatMessage[] = [
    {
      id: 'debug-msg-1',
      authorId: DEBUG_ADMIN_COUNTERPARTY.uid,
      authorName: DEBUG_ADMIN_COUNTERPARTY.displayName,
      text: `Welcome aboard! I've assigned the inventory check + the vendor call. Ping me here once you're done.`,
      timestampIso: new Date(now - 25 * 60 * 1000).toISOString(),
    },
    {
      id: 'debug-msg-2',
      authorId: DEBUG_USER.uid,
      authorName: DEBUG_HELPER_DISPLAY_NAME,
      text: `On it. Heading to the storage room first.`,
      timestampIso: new Date(now - 18 * 60 * 1000).toISOString(),
    },
    {
      id: 'debug-msg-3',
      authorId: DEBUG_ADMIN_COUNTERPARTY.uid,
      authorName: DEBUG_ADMIN_COUNTERPARTY.displayName,
      text: `Perfect — we have a 4 PM sync on the calendar to align before tomorrow.`,
      timestampIso: new Date(now - 5 * 60 * 1000).toISOString(),
    },
  ]
  ;(window as unknown as Record<string, unknown>)[CHAT_STORE_KEY] = seeded
  return seeded
}

const emitChatChange = (): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CHAT_EVENT))
}

export const subscribeDebugChat = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {}
  const handler = () => callback()
  window.addEventListener(CHAT_EVENT, handler)
  return () => window.removeEventListener(CHAT_EVENT, handler)
}

export const getDebugHelperChatMessages = (): DebugChatMessage[] => getChatStore().slice()

export const appendDebugHelperChatMessage = (params: {
  authorId: string
  authorName: string
  text: string
}): DebugChatMessage => {
  const store = getChatStore()
  const message: DebugChatMessage = {
    id: `debug-msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    authorId: params.authorId,
    authorName: params.authorName,
    text: params.text,
    timestampIso: new Date().toISOString(),
  }
  store.push(message)
  emitChatChange()
  return message
}
